import type { SerializedTableState } from '@/components/shared/fancyTables';
import { useFancyTablesStore } from '@/stores/fancy-tables.store';
import { debounce, isEqual } from 'lodash';
import { onMounted, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApiUtils } from './api-utils.composable';

interface TableConfigState extends SerializedTableState {
  id?: number;
  name?: string;
  isDefault?: boolean;
}

interface UseFancyServerTableConfigOptions {
  tableName: string;
  fancyTableRef: Ref<any>;
  onConfigApplied?: (config: TableConfigState) => Promise<void>;
}

export function useFancyServerTableConfig(options: UseFancyServerTableConfigOptions) {
  const { tableName, fancyTableRef, onConfigApplied } = options;
  const isStateLoaded = ref(false);
  const { t } = useI18n();
  const {
    getSavedTableStates,
    saveTableState,
    updateTableState,
    deleteTableState,
    markTableStateAsDefault,
  } = useFancyTablesStore();
  const { handleApiError } = useApiUtils();

  // Reactive state
  const savedTableConfigs = ref<TableConfigState[]>([]);
  const currentSavedState = ref<TableConfigState | undefined>(undefined);
  const canSaveState = ref(false);
  const configLoading = ref(false);

  /**
   * Loads saved table configurations from the server
   */
  const loadSavedConfig = async (): Promise<void> => {
    try {
      configLoading.value = true;
      savedTableConfigs.value = await getSavedTableStates(tableName);

      if (savedTableConfigs.value.length === 0) {
        return;
      }

      // Try to find default config first, otherwise use the first one
      const defaultConfig = savedTableConfigs.value.find(config => config.isDefault);

      const configToApply = defaultConfig || savedTableConfigs.value[0];
      currentSavedState.value = configToApply;

      // Apply the default config to the table if available
      if (configToApply && fancyTableRef.value?.updateTableState) {
        fancyTableRef.value.updateTableState(configToApply);

        // Call the onConfigApplied callback if provided (for server-side tables to fetch data)
        if (onConfigApplied) {
          await onConfigApplied(configToApply);
        }
      }
    } catch (error) {
      console.error('Failed to load saved table configurations:', error);
    } finally {
      isStateLoaded.value = true;
      configLoading.value = false;
    }
  };

  /**
   * Handles table state changes and determines if save state should be enabled
   */
  const handleTableStateChange = (state: SerializedTableState): void => {
    if (!currentSavedState.value) {
      canSaveState.value = true;
      return;
    }

    const relevantKeys: (keyof SerializedTableState)[] = [
      'columnVisibility',
      'columnOrder',
      'columnSizing',
      'columnPinning',
      'columnFilters',
      'sorting',
      'pageSize',
    ];

    const hasAnyChanges = relevantKeys.some(key => {
      return !isEqual(state[key], currentSavedState.value?.[key]);
    });

    canSaveState.value = hasAnyChanges;
  };

  const debouncedHandleTableStateChange = debounce(handleTableStateChange, 300);

  /**
   * Saves a new table configuration
   */
  const handleSaveConfig = async (configName: string, setAsDefault: boolean): Promise<void> => {
    if (!fancyTableRef.value) {
      throw new Error('Table reference is not available');
    }

    configLoading.value = true;
    const payload = {
      ...fancyTableRef.value.getSerializedState(),
      name: configName,
      isDefault: setAsDefault,
    };

    try {
      const response = await handleApiError(
        () => saveTableState(tableName, payload),
        t('common.forms.changes_saved')
      );

      // Refresh saved configs and update current state
      savedTableConfigs.value = await getSavedTableStates(tableName);
      const currentConfigIndex = savedTableConfigs.value.findIndex(
        config => config.id === response.id
      );
      if (currentConfigIndex !== -1) {
        currentSavedState.value = savedTableConfigs.value[currentConfigIndex];
      }

      canSaveState.value = false;
    } catch (error) {
      console.error('Failed to save table configuration:', error);
      throw error;
    } finally {
      configLoading.value = false;
    }
  };

  /**
   * Applies an existing table configuration
   */
  const handleApplyConfig = async (configId: number): Promise<void> => {
    if (!fancyTableRef.value) {
      throw new Error('Table reference is not available');
    }

    configLoading.value = true;
    try {
      const configToApply = savedTableConfigs.value.find(config => config.id === configId);

      if (!configToApply) {
        throw new Error(`Configuration with ID ${configId} not found`);
      }

      currentSavedState.value = configToApply;
      fancyTableRef.value.updateTableState(currentSavedState.value);
      canSaveState.value = false;

      // Call the onConfigApplied callback if provided (for server-side tables to fetch data)
      if (onConfigApplied) {
        await onConfigApplied(configToApply);
      }
    } catch (error) {
      console.error('Failed to apply table configuration:', error);
      throw error;
    } finally {
      configLoading.value = false;
    }
  };

  /**
   * Updates an existing table configuration
   */
  const handleUpdateConfig = async (configId: number): Promise<void> => {
    if (!fancyTableRef.value) {
      throw new Error('Table reference is not available');
    }

    configLoading.value = true;
    const payload = {
      ...fancyTableRef.value.getSerializedState(),
    };

    try {
      await handleApiError(
        () => updateTableState(configId, payload),
        t('common.forms.changes_saved')
      );

      // Refresh saved configs and update current state
      savedTableConfigs.value = await getSavedTableStates(tableName);
      currentSavedState.value = savedTableConfigs.value.find(config => config.id === configId);
      canSaveState.value = false;
    } catch (error) {
      console.error('Failed to update table configuration:', error);
      throw error;
    } finally {
      configLoading.value = false;
    }
  };

  /**
   * Refreshes the saved table configurations from the server
   */
  const refreshSavedConfigs = async (): Promise<void> => {
    try {
      savedTableConfigs.value = await getSavedTableStates(tableName);
    } catch (error) {
      console.error('Failed to refresh saved table configurations:', error);
      throw error;
    }
  };

  /**
   * Deletes a table configuration
   */
  const handleDeleteConfig = async (configId: number): Promise<void> => {
    configLoading.value = true;
    try {
      const wasCurrentConfig = currentSavedState.value?.id === configId;

      await handleApiError(() => deleteTableState(configId), t('common.forms.changes_saved'));

      // Refresh the configs list
      await refreshSavedConfigs();

      // If we deleted the currently selected config, select a new one
      if (wasCurrentConfig && savedTableConfigs.value.length > 0) {
        // Try to find default config first, otherwise use the first one
        const defaultConfig = savedTableConfigs.value.find(config => config.isDefault);
        const newConfig = defaultConfig || savedTableConfigs.value[0];
        currentSavedState.value = newConfig;

        // Apply the new config to the table
        if (newConfig.id) {
          await handleApplyConfig(newConfig.id);
        }
      } else if (savedTableConfigs.value.length === 0) {
        // No configs left, clear current state
        currentSavedState.value = undefined;
        canSaveState.value = true;
      }
    } catch (error) {
      console.error('Failed to delete table configuration:', error);
      throw error;
    } finally {
      configLoading.value = false;
    }
  };

  /**
   * Marks a configuration as default
   */
  const handleMarkAsDefault = async (configId: number): Promise<void> => {
    configLoading.value = true;
    try {
      await handleApiError(
        () => markTableStateAsDefault(configId),
        t('common.forms.changes_saved')
      );
      await refreshSavedConfigs();
    } catch (error) {
      console.error('Failed to mark table configuration as default:', error);
      throw error;
    } finally {
      configLoading.value = false;
    }
  };

  // Initialize on mount
  onMounted(async () => {
    await loadSavedConfig();
  });

  return {
    // Reactive state
    savedTableConfigs,
    currentSavedState,
    canSaveState,
    isStateLoaded,
    configLoading,

    // Methods
    loadSavedConfig,
    handleTableStateChange,
    debouncedHandleTableStateChange,
    handleSaveConfig,
    handleApplyConfig,
    handleUpdateConfig,
    refreshSavedConfigs,
    handleDeleteConfig,
    handleMarkAsDefault,
  };
}
